import { ActiveUnit, Transaction, UnitRow } from 'types';
import { UnitActivity } from 'types/audit/unit-activity';
import { joinRsql, LogicalOperator } from 'utils';
import { downloadCsv } from './download-csv';
import { fetchPaginated } from './fetch-paginated';
import { refreshToken } from './auth';
import fetchWithRetry from './fetch-retry';

export const getUnits = async (locationId?: string, customerId?: string): Promise<ActiveUnit[]> => {
  const url =
    '/api/units' +
    (locationId
      ? customerId
        ? `?locationId=${locationId}&customerId=${customerId}`
        : `?locationId=${locationId}`
      : customerId
      ? `?customerId=${customerId}`
      : '');
  return await fetchWithRetry<ActiveUnit[]>(url, { method: 'GET' }, []);
};

export const fetchActiveUnitList = fetchPaginated<UnitRow>('units');

type UngroupedUnitSpecification = {
  active?: boolean;
  associatedUnitId?: number | string;
  bookingId?: number | string;
  redeliveryId?: number | string;
  /* sent as the transactionId query param - the API scopes by it, so it adds no RSQL */
  transactionId?: number | string;
};

const getAdditionalFilter = (specification: Omit<UngroupedUnitSpecification, 'transactionId'>) => {
  if (specification.active) {
    return `active==true`;
  } else if (specification.associatedUnitId) {
    return (
      `(baseUnit.id==${specification.associatedUnitId},` +
      `mountedUnits.id==${specification.associatedUnitId},` +
      `baseUnit.mountedUnits.id==${specification.associatedUnitId});` +
      `(id>${specification.associatedUnitId},id<${specification.associatedUnitId})` /* integer not-equal */
    );
  } else if (specification.bookingId) {
    return `booking.id==${specification.bookingId}`;
  } else if (specification.redeliveryId) {
    return `redelivery.id==${specification.redeliveryId}`;
  }
  return '';
};

export const fetchActiveUnitsUngrouped =
  ({ transactionId, ...specification }: UngroupedUnitSpecification): ReturnType<typeof fetchPaginated<ActiveUnit>> =>
  async params => {
    const filterString = joinRsql(LogicalOperator.AND, params.filterString, getAdditionalFilter(specification));
    return fetchPaginated<ActiveUnit>(
      'units/ungrouped',
      !transactionId ? undefined : { transactionId }
    )({ ...params, filterString });
  };

export const downloadUngroupedCsv = ({
  transactionId,
  ...specification
}: UngroupedUnitSpecification): ReturnType<typeof downloadCsv> => {
  return async (...params) => {
    const filterString = joinRsql(LogicalOperator.AND, params[1], getAdditionalFilter(specification));
    return downloadCsv(`units/ungrouped`, !transactionId ? undefined : { transactionId: `${transactionId}` })(
      params[0],
      filterString,
      params[2],
      params[3],
      params[4]
    );
  };
};

export const getUnit = async (id: number): Promise<ActiveUnit | null> => {
  const url = new URL(`/api/units/${id}`, window.location.origin);
  return await fetchWithRetry<ActiveUnit | null>(url, { method: 'GET' }, null);
};

export const getReceivedDate = async (id: number): Promise<string | null> => {
  const url = new URL(`/api/units/${id}/received-date`, window.location.origin);
  return await fetchWithRetry<string | null>(url, { method: 'GET' }, null);
};

export const getGateTransactionsById = async (id: number): Promise<Transaction[]> => {
  const url = new URL(`/api/units/${id}/transactions`, window.location.origin);
  return await fetchWithRetry<Transaction[]>(url, { method: 'GET' }, []);
};

export const getBaseUnit = async (id: number): Promise<ActiveUnit | null> => {
  const url = new URL(`/api/units/${id}/baseUnit`, window.location.origin);
  return await fetchWithRetry<ActiveUnit | null>(url, { method: 'GET' }, null);
};

const fetchWithRetryNoLogOn404 = async <T>(url: URL, defaultValue: T): Promise<T> => {
  try {
    let token = localStorage.getItem('token');
    if (!token) {
      return defaultValue;
    }

    const makeInit = (t: string): RequestInit => ({
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${t}` })
    });

    // first attempt
    let response = await fetch(url, makeInit(token));
    if (response.status === 404) {
      return defaultValue;
    }
    if (response.ok) {
      return response.json().catch(() => defaultValue);
    }

    // refresh & retry only on auth errors
    if (response.status === 401 || response.status === 403) {
      token = await refreshToken();
      if (!token) {
        return defaultValue;
      }
      response = await fetch(url, makeInit(token));
      if (response.status === 404) {
        return defaultValue;
      }
      if (response.ok) {
        return response.json().catch(() => defaultValue);
      }
    }
  } catch (_) {
    // intentionally quiet: these endpoints are used for validation/autocomplete
  }
  return defaultValue;
};

export const getUnitByUnitNumber = async (unitNumber: string): Promise<ActiveUnit | null> => {
  const url = new URL(`/api/units/unitNumber/${unitNumber}`, window.location.origin);
  return await fetchWithRetryNoLogOn404<ActiveUnit | null>(url, null);
};

export const getReservedUnitByUnitNumber = async (unitNumber: string): Promise<ActiveUnit | null> => {
  const url = new URL(`/api/units/reserved/unitNumber/${unitNumber}`, window.location.origin);
  return await fetchWithRetryNoLogOn404<ActiveUnit | null>(url, null);
};

export const searchByUnitNumber = async (unitNumber: string, date?: Date) => {
  const url = new URL(`/api/units/unitNumberSearch/${unitNumber}`, window.location.origin);
  if (date) {
    url.searchParams.append('date', date.toISOString());
  }
  return await fetchWithRetryNoLogOn404<ActiveUnit | null>(url, null);
};

export const getAssociatedUnits = async (id: number): Promise<ActiveUnit[]> => {
  const url = new URL(`/api/units/${id}/associated`, window.location.origin);
  return await fetchWithRetry<ActiveUnit[]>(url, { method: 'GET' }, []);
};

export const updateUnit = async (id: number, unit: ActiveUnit): Promise<ActiveUnit | null> => {
  const init: RequestInit = {
    method: 'PUT',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(unit)
  };

  return await fetchWithRetry<ActiveUnit | null>(`/api/units/${id}`, init, null);
};

export const updateUnitSizeType = async (id: number, sizeType: string): Promise<ActiveUnit | null> => {
  const init: RequestInit = {
    method: 'PUT',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ sizeType })
  };

  return await fetchWithRetry<ActiveUnit | null>(`/api/units/${id}/size-type`, init, null);
};

export const getUnitActivity = async (id: number): Promise<UnitActivity[]> => {
  const url = new URL(`/api/units/${id}/activity`, window.location.origin);
  return await fetchWithRetry<UnitActivity[]>(url, { method: 'GET' }, []);
};

export const resendLiftOnEdi = async (unitId: number, recipientId: number): Promise<void> => {
  const url = new URL(`/api/units/${unitId}/resend-lift-on-edi/${recipientId}`, window.location.origin);
  fetchWithRetry<void>(url, { method: 'POST' }, undefined);
};

export const resendLiftOffEdi = async (unitId: number, recipientId: number): Promise<void> => {
  const url = new URL(`/api/units/${unitId}/resend-lift-off-edi/${recipientId}`, window.location.origin);
  fetchWithRetry<void>(url, { method: 'POST' }, undefined);
};

export const resendStatusEdi = async (unitId: number, recipientId: number): Promise<void> => {
  const url = new URL(`/api/units/${unitId}/resend-status-edi/${recipientId}`, window.location.origin);
  fetchWithRetry<void>(url, { method: 'POST' }, undefined);
};
